/**
 * What the automation (ring R4) adds to an AI call campaign: whether the
 * automation may work it, how often a company is tried, between which dates,
 * who gets its tasks, and what the queue rows say about the calls already made.
 *
 * Its own file rather than more of `aiCampaigns.ts`, which mirrors the server's
 * campaign API and sits at the size limit. `aiCampaigns.ts` composes the draft,
 * body and patch pieces below into its own `toCampaignDraft` / `toCampaignBody`
 * / `toCampaignPatch`. Only types come from there, the way `aiCampaignAngle.ts`
 * does it, so the two files never load each other at runtime: the bounds
 * (`CAMPAIGN_LIMITS`) arrive as an argument, read inside the functions, and
 * nothing at module level depends on them.
 *
 * A HAND-WRITTEN MIRROR of `TARGET_SKIP_REASONS`
 * (`apps/api/src/modules/crm-calls/lib/outbound/auto/target-outcome.ts`).
 * `aiCampaignAutomation.test.ts` reads the server file and holds the list
 * against it, so a skip reason added on the server fails a test here instead
 * of rendering a queue row as a raw key.
 *
 * The "call the next one now" button has its own file
 * (`aiCampaignCallNext.ts`), re-exported at the bottom.
 *
 * React-free: the draft, the date check and the queue row facts are the parts
 * worth testing, and they run under plain node.
 */
import type { AiCallTarget, CampaignAngle } from "./aiCampaigns";

// ---------------------------------------------------------------- mirror

/**
 * Why a queued company left the queue without (another) call. The first 17 are
 * the audience screen's refusals (`AUDIENCE_SKIP_REASONS`, same order), the
 * rest come from placing the call and from the automation.
 */
export const TARGET_SKIP_REASONS = [
  "no_company",
  "sole_trader",
  "deleted_company",
  "existing_client",
  "brain_refused",
  "domain_refused",
  "no_domain",
  "no_phone",
  "invalid_phone",
  "foreign_phone",
  "mobile_excluded",
  "suppressed",
  "record_objected",
  "cooldown",
  "line_invalid",
  "already_in_campaign",
  "duplicate_phone",
  "phone_changed",
  "own_line",
  "defect_gone",
  "alert_too_old",
  "alert_called",
  "twilio_blocked",
  "site_excluded",
] as const;
export type TargetSkipReason = (typeof TARGET_SKIP_REASONS)[number];

/** A whole-number field's bounds, as `CAMPAIGN_LIMITS` holds them. */
export interface CountBounds {
  min: number;
  max: number;
  fallback: number;
}

/** The two bounds the automation half reads. `CAMPAIGN_LIMITS` is one of these. */
export interface AutomationLimits {
  maxAttempts: CountBounds;
  retryAfterHours: CountBounds;
}

/** The automation fields of `AiCallCampaignView`. Optional: an older server sends none of them. */
export interface CampaignAutomationView {
  autoEnabled?: boolean;
  maxAttempts?: number;
  retryAfterHours?: number;
  /** ISO, or null for no start date. */
  startsAt?: string | null;
  /** ISO, or null for no end date. */
  endsAt?: string | null;
  /** A staff id, or null for the campaign's creator. */
  assigneeId?: string | null;
}

/** The automation fields of a create body and a patch. */
export interface CampaignAutomationBody {
  autoEnabled: boolean;
  /** Left out for an alert campaign: one call per defect, never a retry. */
  maxAttempts?: number;
  retryAfterHours?: number;
  startsAt: string | null;
  endsAt: string | null;
  assigneeId: string | null;
}

// ---------------------------------------------------------------- draft

/** The automation half of the editor's draft, as typed. */
export interface CampaignAutomationDraft {
  autoEnabled: boolean;
  /** Typed text; checked on the way out. */
  maxAttempts: string;
  /** Typed text; checked on the way out. */
  retryAfterHours: string;
  /** The picker's ISO moment, "" for none. */
  startsAt: string;
  /** The picker's ISO moment, "" for none. */
  endsAt: string;
  /** A staff id, "" for the campaign's creator. */
  assigneeId: string;
}

/** A new campaign: the automation off, the server's default tries, no dates, the creator gets the tasks. */
export function emptyAutomationDraft(limits: AutomationLimits): CampaignAutomationDraft {
  return {
    autoEnabled: false,
    maxAttempts: String(limits.maxAttempts.fallback),
    retryAfterHours: String(limits.retryAfterHours.fallback),
    startsAt: "",
    endsAt: "",
    assigneeId: "",
  };
}

/**
 * A saved campaign's automation fields as the draft holds them. A field an
 * older server did not send reads as the default a new campaign gets, which is
 * also what the database holds for it.
 */
export function toAutomationDraft(c: CampaignAutomationView, limits: AutomationLimits): CampaignAutomationDraft {
  return {
    autoEnabled: c.autoEnabled === true,
    maxAttempts: String(c.maxAttempts ?? limits.maxAttempts.fallback),
    retryAfterHours: String(c.retryAfterHours ?? limits.retryAfterHours.fallback),
    startsAt: c.startsAt ?? "",
    endsAt: c.endsAt ?? "",
    assigneeId: c.assigneeId ?? "",
  };
}

/** Typed digits as a whole number inside the bounds, or null when they are not one. */
export function readCount(text: string, bounds: CountBounds): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= bounds.min && n <= bounds.max ? n : null;
}

/**
 * A picked moment as the ISO the server stores: "" is no date (null), and text
 * that is not a moment is undefined, which makes the draft invalid rather than
 * silently dropping a date the owner set.
 */
export function readMoment(text: string): string | null | undefined {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const ms = Date.parse(trimmed);
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

/**
 * The server's `bad_dates` rule: with both dates set, the end must come after
 * the start. Either date missing is always fine.
 */
export function datesInOrder(startsAt: string | null, endsAt: string | null): boolean {
  if (!startsAt || !endsAt) return true;
  return Date.parse(endsAt) > Date.parse(startsAt);
}

/** Whether the retry fields mean anything: an alert campaign rings once per defect. */
export function usesRetries(angle: CampaignAngle): boolean {
  return angle !== "alert";
}

/**
 * The automation half of the create body, or null when a field is not valid.
 *
 * An alert campaign leaves the retry numbers out, as `toCampaignBody` leaves
 * out its reason slots: the editor hides them, so a hidden unreadable number
 * must not refuse a save the owner cannot see, and the server finishes an
 * alert target after one call whatever they say.
 */
export function toAutomationBody(d: CampaignAutomationDraft, angle: CampaignAngle, limits: AutomationLimits): CampaignAutomationBody | null {
  const startsAt = readMoment(d.startsAt);
  const endsAt = readMoment(d.endsAt);
  if (startsAt === undefined || endsAt === undefined || !datesInOrder(startsAt, endsAt)) return null;
  const body: CampaignAutomationBody = {
    autoEnabled: d.autoEnabled,
    startsAt,
    endsAt,
    assigneeId: d.assigneeId.trim() || null,
  };
  if (!usesRetries(angle)) return body;
  const maxAttempts = readCount(d.maxAttempts, limits.maxAttempts);
  const retryAfterHours = readCount(d.retryAfterHours, limits.retryAfterHours);
  if (maxAttempts === null || retryAfterHours === null) return null;
  return { ...body, maxAttempts, retryAfterHours };
}

/** Two stored moments are the same instant (an ISO may come back with other digits). */
function sameMoment(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Date.parse(a) === Date.parse(b);
}

/**
 * Only the automation fields that changed against the saved campaign, or null
 * when the draft is not valid. A field an older save lacks compares against
 * the default it stands for, so an untouched old campaign patches nothing.
 */
export function toAutomationPatch(
  d: CampaignAutomationDraft,
  saved: CampaignAutomationView,
  angle: CampaignAngle,
  limits: AutomationLimits,
): Partial<CampaignAutomationBody> | null {
  const body = toAutomationBody(d, angle, limits);
  if (!body) return null;
  const patch: Partial<CampaignAutomationBody> = {};
  if (body.autoEnabled !== (saved.autoEnabled ?? false)) patch.autoEnabled = body.autoEnabled;
  if (body.maxAttempts !== undefined && body.maxAttempts !== (saved.maxAttempts ?? limits.maxAttempts.fallback)) {
    patch.maxAttempts = body.maxAttempts;
  }
  if (body.retryAfterHours !== undefined && body.retryAfterHours !== (saved.retryAfterHours ?? limits.retryAfterHours.fallback)) {
    patch.retryAfterHours = body.retryAfterHours;
  }
  if (!sameMoment(body.startsAt, saved.startsAt ?? null)) patch.startsAt = body.startsAt;
  if (!sameMoment(body.endsAt, saved.endsAt ?? null)) patch.endsAt = body.endsAt;
  if (body.assigneeId !== (saved.assigneeId ?? null)) patch.assigneeId = body.assigneeId;
  return patch;
}

/**
 * The assignee menu: "" (the creator) first, then the staff, then the saved id
 * when the directory does not hold it, so a removed person is still shown as
 * the one set rather than as a blank field that reads as the creator.
 */
export function assigneeOptions(staff: readonly { id: string }[], current: string): Array<{ value: string; kind: "creator" | "staff" | "missing" }> {
  const options: Array<{ value: string; kind: "creator" | "staff" | "missing" }> = [{ value: "", kind: "creator" }];
  const seen = new Set<string>([""]);
  for (const person of staff) {
    if (seen.has(person.id)) continue;
    seen.add(person.id);
    options.push({ value: person.id, kind: "staff" });
  }
  const id = current.trim();
  if (id && !seen.has(id)) options.push({ value: id, kind: "missing" });
  return options;
}

// ---------------------------------------------------------------- queue rows

export interface TargetRowFacts {
  /** Calls placed to this company; null before the first. */
  attempts: number | null;
  /** When the next try is due: only for a row waiting on a retry. */
  nextAttemptAt: string | null;
  /** Suffix under `aiCalls.campaigns.targetSkip` for a skipped row: the reason, or "unknown". */
  skipKey: string | null;
  /** The last call's word as stored, or null while there is none. */
  disposition: string | null;
}

const KNOWN_SKIPS: ReadonlySet<string> = new Set<string>(TARGET_SKIP_REASONS);

/** What a queue row says beside its status. */
export function targetRowFacts(
  row: Pick<AiCallTarget, "status" | "attempts" | "skipReason" | "disposition"> & { nextAttemptAt?: string | null },
): TargetRowFacts {
  const skip = row.status === "skipped" ? (row.skipReason ?? "") : null;
  return {
    attempts: row.attempts > 0 ? row.attempts : null,
    nextAttemptAt: row.status === "retry" ? (row.nextAttemptAt ?? null) : null,
    skipKey: skip === null ? null : KNOWN_SKIPS.has(skip) ? skip : "unknown",
    disposition: row.disposition,
  };
}

// ---------------------------------------------------------------- call next now

// The button's request and note live in `aiCampaignCallNext.ts`; re-exported so
// the automation tab's import of the request and its reply type stays here.
export {
  CALL_NEXT_ERRORS,
  callNextAiCampaign,
  callNextNote,
  callNextReasonKey,
  type CallNextNote,
  type CallNextReply,
  type CallNextReport,
} from "./aiCampaignCallNext";
